/**
 * Permission system for admin users
 * Defines what each admin level can access
 */

export type AdminLevel = 'church_owner' | 'church_admin' | 'church_elder' | 'ministry_leader' | 'none'

export interface Permission {
  canViewDashboard: boolean
  canViewMembers: boolean
  canAddMembers: boolean
  canEditMembers: boolean
  canDeleteMembers: boolean
  canViewFinance: boolean
  canManageFinance: boolean
  canViewEvents: boolean
  canManageEvents: boolean
  canViewCommunications: boolean
  canSendCommunications: boolean
  canViewReports: boolean
  canManageAdmins: boolean
  canManageSettings: boolean
  canViewProfile: boolean
}

/**
 * Get permissions based on admin level
 */
export function getPermissions(adminLevel: AdminLevel): Permission {
  switch (adminLevel) {
    case 'church_owner':
      // Full access to everything
      return {
        canViewDashboard: true,
        canViewMembers: true,
        canAddMembers: true,
        canEditMembers: true,
        canDeleteMembers: true,
        canViewFinance: true,
        canManageFinance: true,
        canViewEvents: true,
        canManageEvents: true,
        canViewCommunications: true,
        canSendCommunications: true,
        canViewReports: true,
        canManageAdmins: true,
        canManageSettings: true,
        canViewProfile: true,
      }

    case 'church_admin':
      // Can manage most things except admins
      return {
        canViewDashboard: true,
        canViewMembers: true,
        canAddMembers: true,
        canEditMembers: true,
        canDeleteMembers: true,
        canViewFinance: true,
        canManageFinance: true,
        canViewEvents: true,
        canManageEvents: true,
        canViewCommunications: true,
        canSendCommunications: true,
        canViewReports: true,
        canManageAdmins: false,
        canManageSettings: true,
        canViewProfile: true,
      }

    case 'church_elder':
      // Can view most things, manage members and events, but not finances
      return {
        canViewDashboard: true,
        canViewMembers: true,
        canAddMembers: true,
        canEditMembers: true,
        canDeleteMembers: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewEvents: true,
        canManageEvents: true,
        canViewCommunications: true,
        canSendCommunications: true,
        canViewReports: true,
        canManageAdmins: false,
        canManageSettings: false,
        canViewProfile: true,
      }

    case 'ministry_leader':
      // Limited to viewing and basic member management
      return {
        canViewDashboard: true,
        canViewMembers: true,
        canAddMembers: true,
        canEditMembers: true,
        canDeleteMembers: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewEvents: true,
        canManageEvents: true,
        canViewCommunications: true,
        canSendCommunications: false,
        canViewReports: false,
        canManageAdmins: false,
        canManageSettings: false,
        canViewProfile: true,
      }

    default:
      // No permissions
      return {
        canViewDashboard: false,
        canViewMembers: false,
        canAddMembers: false,
        canEditMembers: false,
        canDeleteMembers: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewEvents: false,
        canManageEvents: false,
        canViewCommunications: false,
        canSendCommunications: false,
        canViewReports: false,
        canManageAdmins: false,
        canManageSettings: false,
        canViewProfile: false,
      }
  }
}

/**
 * Check if user has specific permission
 */
export function hasPermission(adminLevel: AdminLevel | undefined, permission: keyof Permission): boolean {
  if (!adminLevel) return false
  const permissions = getPermissions(adminLevel)
  return permissions[permission]
}

/**
 * Get display name for admin level
 */
export function getAdminLevelDisplay(adminLevel: AdminLevel): string {
  switch (adminLevel) {
    case 'church_owner':
      return 'Church Owner'
    case 'church_admin':
      return 'Church Admin'
    case 'church_elder':
      return 'Church Elder'
    case 'ministry_leader':
      return 'Ministry Leader'
    default:
      return 'User'
  }
}

/**
 * Get description for admin level
 */
export function getAdminLevelDescription(adminLevel: AdminLevel): string {
  switch (adminLevel) {
    case 'church_owner':
      return 'Full access to all features, settings, and admin management'
    case 'church_admin':
      return 'Can manage members, events, finances, and communications'
    case 'church_elder':
      return 'Can manage members and events, view reports, no finance access'
    case 'ministry_leader':
      return 'Can view and manage members/events in their ministry area'
    default:
      return 'Basic user access'
  }
}
